import { Connection } from '../../types/device';

export class TcpConnection implements Connection {
  private readonly ipAddress: string;
  private readonly port: number;
  private connected: boolean = false;
  private eventListeners: Map<string, Function[]> = new Map();

  constructor(ipAddress: string, port: number) {
    this.ipAddress = ipAddress;
    this.port = port;
  }

  public async connect(): Promise<void> {
    // TCP连接是由设备主动连接到后端的，前端无法主动建立连接
    // 这里只是检查设备是否已经连接
    try {
      const isConnected = await this.checkConnectionStatus();
      this.connected = isConnected;
      
      if (isConnected) {
        this.emit('connected');
      } else {
        throw new Error(`设备 ${this.ipAddress}:${this.port} 未连接到服务器`);
      }
    } catch (error) {
      console.error('检查TCP连接状态失败:', error);
      this.connected = false;
      this.emit('error', error);
      throw error;
    }
  }

  public async disconnect(): Promise<void> {
    // TCP连接断开通常由设备主动断开，或者后端强制断开
    // 前端可以请求后端移除连接
    try {
      const baseUrl = this.getBaseUrl();
      const response = await fetch(`${baseUrl}/api/tcp/connection/${this.ipAddress}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      this.connected = false;
      this.emit('disconnected');
    } catch (error) {
      console.error('请求断开TCP连接失败:', error);
      // 即使API调用失败，也要更新本地状态
      this.connected = false;
      this.emit('disconnected');
      throw error;
    }
  }

  /**
   * 检查设备是否已连接到后端
   */
  private async checkConnectionStatus(): Promise<boolean> {
    try {
      const baseUrl = this.getBaseUrl();
      const response = await fetch(`${baseUrl}/api/tcp/connection/${this.ipAddress}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        return false;
      }

      const result = await response.json();
      return result.success && result.data === true;
    } catch (error) {
      console.error('检查连接状态失败:', error);
      return false;
    }
  }

  public isConnected(): boolean {
    return this.connected;
  }

  /**
   * 更新连接状态（由外部调用，比如从设备列表API获取状态）
   */
  public updateConnectionStatus(connected: boolean): void {
    const wasConnected = this.connected;
    this.connected = connected;
    
    if (connected && !wasConnected) {
      this.emit('connected');
    } else if (!connected && wasConnected) {
      this.emit('disconnected');
    }
  }

  public on(event: string, callback: Function): void {
    if (!this.eventListeners.has(event)) {
      this.eventListeners.set(event, []);
    }
    this.eventListeners.get(event)!.push(callback);
  }

  public off(event: string, callback: Function): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      const index = listeners.indexOf(callback);
      if (index > -1) {
        listeners.splice(index, 1);
      }
    }
  }

  private emit(event: string, data?: any): void {
    const listeners = this.eventListeners.get(event);
    if (listeners) {
      listeners.forEach(callback => callback(data));
    }
  }

  private getBaseUrl(): string {
    const host = window.location.hostname;
    const protocol = window.location.protocol;
    const port = window.location.port;

    if (port === '12346') {
      return `${protocol}//${host}`;
    }

    if (port === '443' || port === '8443' || port === '') {
      return `${protocol}//${host}${port ? ':' + port : ''}`;
    }

    return `${protocol}//${host}`;
  }
}